import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

/**
 * Controls when the revert status/label should be shown:
 *   - 'always' (default): Show the revert action if the status logic matches
 *   - 'onlyIfVoidedOrReconciled': Only show the revert action if the status is Voided or Reconciled
 *   - 'hideIfVoidedOrReconciled': Never show the revert action if the status is Voided or Reconciled
 *   - 'hidden': Never show the revert action (always returns [null, null])
 */
export type RevertVisibility =
  | 'always'
  | 'onlyIfVoidedOrReconciled'
  | 'hideIfVoidedOrReconciled'
  | 'hidden';

/**
 * Parameters for useRevertLoadStatus
 *
 * @property status - The current status of the load (can be undefined/null)
 * @property salesOrderId - The sales order ID associated with the load (can be undefined/null)
 * @property visibility - Controls when the revert status/label should be shown (see RevertVisibility)
 */
export type UseRevertLoadStatusParams = {
  status?: LoadStatus | null;
  salesOrderId?: string | null;
  visibility?: RevertVisibility;
};

/**
 * Returns the next revert status and its i18n label key for a given load status and salesOrderId.
 *
 * This hook is used to determine if and how a "revert" action should be shown for a load, based on its status and assignment.
 *
 * The `visibility` prop allows you to control when the revert action should be available (see RevertVisibility).
 *
 * @param params - See UseRevertLoadStatusParams
 * @returns [revertStatus, revertLabelKey] or [null, null] if not applicable
 */
export const useRevertLoadStatus = (
  params: UseRevertLoadStatusParams,
): [LoadStatus | null, string | null] => {
  const t = useTranslations();
  const { status, salesOrderId, visibility = 'always' } = params;
  return React.useMemo(() => {
    if (visibility === 'hidden') {
      return [null, null];
    }
    if (
      visibility === 'onlyIfVoidedOrReconciled' &&
      status !== enumLoadStatus.Voided &&
      status !== enumLoadStatus.Reconciled
    ) {
      return [null, null];
    }
    if (
      visibility === 'hideIfVoidedOrReconciled' &&
      (status === enumLoadStatus.Voided || status === enumLoadStatus.Reconciled)
    ) {
      return [null, null];
    }
    switch (status) {
      case enumLoadStatus.Reconciled:
        return [enumLoadStatus.Received, t.loadDetail.actions.revertToReceived];
      case enumLoadStatus.Received:
        return [enumLoadStatus.Shipped, t.loadDetail.actions.revertToShipped];
      case enumLoadStatus.Shipped:
        return [
          enumLoadStatus.ContainerReadyToShip,
          t.loadDetail.actions.revertToContainerReadyToShip,
        ];
      case enumLoadStatus.ContainerReadyToShip:
        return [
          enumLoadStatus.ContainerReturning,
          t.loadDetail.actions.revertToContainerReturning,
        ];
      case enumLoadStatus.ContainerReturning:
        return [
          enumLoadStatus.ContainerLoaded,
          t.loadDetail.actions.revertToContainerLoaded,
        ];
      case enumLoadStatus.ContainerLoaded:
        return [
          enumLoadStatus.ContainerAtFacility,
          t.loadDetail.actions.revertToContainerAtFacility,
        ];
      case enumLoadStatus.ContainerAtFacility:
        return [
          enumLoadStatus.ContainerInTransit,
          t.loadDetail.actions.revertToContainerInTransit,
        ];
      case enumLoadStatus.ContainerInTransit:
        return [
          enumLoadStatus.ContainerAtPort,
          t.loadDetail.actions.revertToContainerAtPort,
        ];
      case enumLoadStatus.ContainerAtPort:
        return [enumLoadStatus.Pending, t.loadDetail.actions.revertToPending];
      case enumLoadStatus.Voided:
        return salesOrderId
          ? [enumLoadStatus.Pending, t.loadDetail.actions.revertToPending]
          : [
              enumLoadStatus.Unassigned,
              t.loadDetail.actions.revertToUnassigned,
            ];
      default:
        return [null, null];
    }
  }, [
    visibility,
    status,
    t.loadDetail.actions.revertToReceived,
    t.loadDetail.actions.revertToShipped,
    t.loadDetail.actions.revertToContainerReadyToShip,
    t.loadDetail.actions.revertToContainerReturning,
    t.loadDetail.actions.revertToContainerLoaded,
    t.loadDetail.actions.revertToContainerAtFacility,
    t.loadDetail.actions.revertToContainerInTransit,
    t.loadDetail.actions.revertToContainerAtPort,
    t.loadDetail.actions.revertToPending,
    t.loadDetail.actions.revertToUnassigned,
    salesOrderId,
  ]);
};
